import { create } from 'zustand';
import { TONE } from '../../lib/rcu/catalog/feedback';
import type { Tone } from '../../lib/rcu/catalog/feedback';
import type { MappingEntryReading } from '../../lib/rcu/link/readings';
import { KEYPAD_SEVERITY } from './catalog/issue';
import type { KeypadIssue } from './catalog/issue';
import type { KeyState } from './catalog/keyState';
import {
  addKeypad,
  forgetRetiredAddress,
  removeKeypad,
  renameKey,
  renameKeypad,
  setKeypadAddress,
  updateKeypad,
} from './commands/keypads';
import type { KeypadPatch } from './commands/keypads';
import {
  addLoadToState,
  clearState,
  mirrorAsOff,
  removeLoadFromState,
  setStateLoads,
} from './commands/states';
import { triggerKeyOfVector } from './encode';
import { clearStoredBook, loadStoredBook, saveStoredBook } from './persistence';
import type { KeypadId, KeypadKeyId } from './ids';
import type { Keypad, KeypadBook, KeypadDraft, LoadState } from './model';
import { createEmptyBook } from './model';
import { fail, ok } from './result';
import type { KeypadResult } from './result';
import { absentAnswer, answerFrom, withWritten, withoutWritten, writtenTriggersOf } from './stored';
import type { DeviceAnswers } from './stored';
import type { MappingTarget } from './targets';

/**
 * The keypad book, as the console holds it.
 *
 * Every action delegates to a pure command and only commits when that command
 * succeeded, so the store can never hold a half-applied edit, and each commit is
 * written to storage immediately. Actions return the command's result so the
 * caller can surface the reason for a refusal.
 *
 * What the device holds is *not* state of its own: `answers` is what a read run
 * last brought back, keyed by trigger, and the UI pushes it in. Nothing here
 * invents an answer for a trigger nobody asked about.
 */
export interface KeypadStore {
  readonly book: KeypadBook;
  readonly answers: DeviceAnswers;
  /** Triggers a read run is waiting on, so the UI can show progress. */
  readonly reading: readonly string[];
  /** The last thing worth telling the technician, if anything. */
  readonly notice: KeypadNotice | null;

  addKeypad: (draft: KeypadDraft) => KeypadResult<Keypad>;
  updateKeypad: (keypadId: KeypadId, patch: KeypadPatch) => KeypadResult<Keypad>;
  renameKeypad: (keypadId: KeypadId, name: string) => KeypadResult<Keypad>;
  moveKeypad: (keypadId: KeypadId, address: number) => KeypadResult<Keypad>;
  removeKeypad: (keypadId: KeypadId) => KeypadResult<Keypad>;
  forgetAddress: (keypadId: KeypadId, address: number) => KeypadResult<Keypad>;
  renameKey: (keypadId: KeypadId, keyId: KeypadKeyId, name: string) => KeypadResult<Keypad>;

  setStateLoads: (
    targets: readonly MappingTarget[],
    keypadId: KeypadId,
    keyId: KeypadKeyId,
    state: KeyState,
    loads: readonly LoadState[],
  ) => KeypadResult<Keypad>;
  addLoad: (
    targets: readonly MappingTarget[],
    keypadId: KeypadId,
    keyId: KeypadKeyId,
    state: KeyState,
    load: LoadState,
  ) => KeypadResult<Keypad>;
  removeLoad: (
    targets: readonly MappingTarget[],
    keypadId: KeypadId,
    keyId: KeypadKeyId,
    state: KeyState,
    targetId: string,
  ) => KeypadResult<Keypad>;
  clearState: (
    targets: readonly MappingTarget[],
    keypadId: KeypadId,
    keyId: KeypadKeyId,
    state: KeyState,
  ) => KeypadResult<Keypad>;
  mirrorAsOff: (
    targets: readonly MappingTarget[],
    keypadId: KeypadId,
    keyId: KeypadKeyId,
    from: KeyState,
  ) => KeypadResult<Keypad>;

  /** Records that a read run is waiting on these triggers. */
  startReading: (triggers: readonly string[]) => void;
  /** Records one entry the device answered with. */
  noteReading: (reading: MappingEntryReading) => void;
  /** Records that a read run finished, and that these triggers held nothing. */
  finishReading: (triggers: readonly string[], answered: readonly string[]) => void;
  /** Forgets what was read, e.g. when the link drops. */
  forgetAnswers: () => void;
  /** Remembers what was written, once the transport reports it was delivered. */
  noteWritten: (trigger: string, fingerprint: string) => void;
  /** Forgets what was written at these triggers, after erasing them. */
  forgetWritten: (triggers: readonly string[]) => void;
  /**
   * Replaces the whole book with one that came from outside — an imported file.
   *
   * `answers` is cleared with it: those are what the device said about *this*
   * tablet's panels, keyed by trigger, and a restored book asks about a
   * different set of triggers. Keeping them would show a panel's state as
   * "matches the device" on the strength of an answer about another panel.
   */
  replaceBook: (book: KeypadBook) => void;
  /**
   * Empties the book and removes it from storage.
   *
   * Paired with the configuration's own reset: clearing an installation has to
   * take the panels with it, or the next configuration inherits a book that
   * describes a room it knows nothing about.
   */
  clearBook: () => void;

  setNotice: (notice: KeypadNotice) => void;
  clearNotice: () => void;
}

/** Something worth telling the technician, in the console's voice. */
export interface KeypadNotice {
  readonly tone: Tone;
  readonly text: string;
}

const nowIso = (): string => new Date().toISOString();

/** The notice for a refusal: the issue's own words, at its own severity. */
export const noticeForIssue = (issue: KeypadIssue): KeypadNotice => ({
  tone: issue.severity === KEYPAD_SEVERITY.BLOCKING ? TONE.ERROR : TONE.WARNING,
  text: issue.message,
});

const initialBook = (): KeypadBook => loadStoredBook(nowIso());

export const useKeypadStore = create<KeypadStore>()((set, get) => {
  /** Commits a book, stamping it and writing it to storage. */
  const commit = (book: KeypadBook, now: string): KeypadBook => {
    const next: KeypadBook = { ...book, updatedAt: now };
    set({ book: next, notice: null });
    saveStoredBook(next);
    return next;
  };

  /** Shows why something was refused, and hands the refusal back. */
  const refuse = <T>(issue: KeypadIssue): KeypadResult<T> => {
    set({ notice: noticeForIssue(issue) });
    return fail(issue);
  };

  /**
   * Runs an edit of one panel against the book and commits it when it succeeded.
   * Time is stamped here rather than inside the commands, so a command stays a
   * pure function of the book it is given.
   */
  const run = (
    edit: (book: KeypadBook, now: string) => KeypadResult<Keypad>,
  ): KeypadResult<Keypad> => {
    const now = nowIso();
    const book = get().book;
    const result = edit(book, now);
    if (!result.ok) return refuse(result.issue);

    const keypad = result.value;
    commit(
      {
        ...book,
        keypads: book.keypads.map((entry) => (entry.id === keypad.id ? keypad : entry)),
      },
      now,
    );
    return ok(keypad);
  };

  return {
    book: initialBook(),
    answers: {},
    reading: [],
    notice: null,

    addKeypad: (draft) => {
      const now = nowIso();
      const book = get().book;
      const added = addKeypad(book, draft, now);
      if (!added.ok) return refuse(added.issue);
      commit({ ...book, keypads: [...book.keypads, added.value] }, now);
      return added;
    },

    updateKeypad: (keypadId, patch) => run((book) => updateKeypad(book, keypadId, patch)),
    renameKeypad: (keypadId, name) => run((book) => renameKeypad(book, keypadId, name)),
    moveKeypad: (keypadId, address) => run((book) => setKeypadAddress(book, keypadId, address)),
    renameKey: (keypadId, keyId, name) => run((book) => renameKey(book, keypadId, keyId, name)),
    forgetAddress: (keypadId, address) =>
      run((book) => forgetRetiredAddress(book, keypadId, address)),

    removeKeypad: (keypadId) => {
      const book = get().book;
      const removed = removeKeypad(book, keypadId);
      if (!removed.ok) {
        set({ notice: noticeForIssue(removed.issue) });
        return removed;
      }

      // What the device holds at that panel's triggers stays there until it is
      // erased, but this tablet stops remembering it as its own — and stops
      // claiming to know what the device holds there.
      const triggers = writtenTriggersOf(book, removed.value);
      const answers = { ...get().answers };
      for (const trigger of triggers) delete answers[trigger];
      set({ answers });
      commit(
        {
          ...withoutWritten(book, triggers),
          keypads: book.keypads.filter((entry) => entry.id !== keypadId),
        },
        nowIso(),
      );
      return removed;
    },

    setStateLoads: (targets, keypadId, keyId, state, loads) =>
      run((book) => setStateLoads(book, targets, keypadId, keyId, state, loads)),
    addLoad: (targets, keypadId, keyId, state, load) =>
      run((book) => addLoadToState(book, targets, keypadId, keyId, state, load)),
    removeLoad: (targets, keypadId, keyId, state, targetId) =>
      run((book) => removeLoadFromState(book, targets, keypadId, keyId, state, targetId)),
    clearState: (targets, keypadId, keyId, state) =>
      run((book) => clearState(book, targets, keypadId, keyId, state)),
    mirrorAsOff: (targets, keypadId, keyId, from) =>
      run((book) => mirrorAsOff(book, targets, keypadId, keyId, from)),

    startReading: (triggers) => set({ reading: [...new Set(triggers)] }),

    noteReading: (reading) =>
      set((state) => ({
        answers: { ...state.answers, [triggerKeyOfVector(reading.x)]: answerFrom(reading) },
      })),

    finishReading: (triggers, answered) =>
      set((state) => {
        const answers = { ...state.answers };
        for (const trigger of triggers) {
          if (!answered.includes(trigger)) answers[trigger] = absentAnswer();
        }
        return { answers, reading: [] };
      }),

    /** Nothing is known about the device again until the next read run. */
    forgetAnswers: () => set({ answers: {}, reading: [] }),

    noteWritten: (trigger, fingerprint) => {
      const book = withWritten(get().book, trigger, fingerprint);
      set({ book });
      saveStoredBook(book);
    },

    forgetWritten: (triggers) => {
      const book = withoutWritten(get().book, triggers);
      set({ book });
      saveStoredBook(book);
    },

    replaceBook: (book) => {
      set({ answers: {}, reading: [] });
      commit(book, nowIso());
    },

    clearBook: () => {
      clearStoredBook();
      set({ book: createEmptyBook(nowIso()), answers: {}, reading: [], notice: null });
    },

    setNotice: (notice) => set({ notice }),
    clearNotice: () => set({ notice: null }),
  };
});
